import { queryOptions, UseQueryOptions } from "@tanstack/react-query";
import { createQueryString, createURLPath } from "@/lib/utils";
import { unwrapApiError } from "@/lib/error";
import { api, ApiOptions, mBaseURL } from "@/config/api";
import { BrandOverviewResponseSchema, CompetitorBenchmarkResponseSchema, CreativesScorecardResponseSchema, LiveMonitorResponseSchema } from "@/schema/brand-os/brand-governance";
import { BrandOverviewResponse, CompetitorBenchmarkResponse, CreativesScorecardResponse, LiveMonitorResponse } from "@/schema/brand-os/brand-governance";

type QueryOptions<T> = Omit<UseQueryOptions<T>, "queryKey" | "queryFn">;

export type CreativesScorecardParams = {
  brand_name: string;
  channels: string[];
  sub_brands: string[];
  period: string;
  start_date?: string;
  end_date?: string;
};

export type LiveMonitorParams = {
  brand_name: string;
};

export type CompetitorBenchmarkParams = {
  brand_name: string;
  period?: string;
  start_date?: string;
  end_date?: string;
};

export const BrandGovernanceApiFactory = {
  Endpoint: mBaseURL + "/api/brand/governance",

  Keys: {
    BrandOverview: (brandName: string) => ["governance", "overview", brandName],
    LiveMonitor: (params: LiveMonitorParams) => ["governance", "creatives", "live-monitor", createQueryString({ brand_name: params.brand_name })],
    CreativesScorecard: (params: CreativesScorecardParams) => {
      const serialized = {
        brand_name: params.brand_name,
        channels: params.channels?.length ? params.channels.join(",") : undefined,
        sub_brands: params.sub_brands?.length ? params.sub_brands.join(",") : undefined,
        period: params.period,
        start_date: params.start_date,
        end_date: params.end_date,
      };
      return ["governance", "creatives", "scorecard", createQueryString(serialized)];
    },
    CompetitorBenchmark: (params: CompetitorBenchmarkParams) => ["governance", "creatives", "competitor-benchmark", createQueryString(params)],
  },

  Queries: {
    BrandOverview: (brandName: string, options?: QueryOptions<BrandOverviewResponse>) =>
      queryOptions({
        queryKey: BrandGovernanceApiFactory.Keys.BrandOverview(brandName),
        queryFn: () => BrandGovernanceApiFactory.Apis.GetBrandOverview(brandName),
        ...options,
      }),

    CreativesScorecard: (params: CreativesScorecardParams, options?: QueryOptions<CreativesScorecardResponse>) =>
      queryOptions({
        queryKey: BrandGovernanceApiFactory.Keys.CreativesScorecard(params),
        queryFn: () => BrandGovernanceApiFactory.Apis.GetCreativesScorecard(params),
        ...options,
      }),

    LiveMonitor: (params: LiveMonitorParams, options?: QueryOptions<LiveMonitorResponse>) =>
      queryOptions({
        queryKey: BrandGovernanceApiFactory.Keys.LiveMonitor(params),
        queryFn: () => BrandGovernanceApiFactory.Apis.GetLiveMonitor(params),
        ...options,
      }),

    CompetitorBenchmark: (params: CompetitorBenchmarkParams, options?: QueryOptions<CompetitorBenchmarkResponse>) =>
      queryOptions({
        queryKey: BrandGovernanceApiFactory.Keys.CompetitorBenchmark(params),
        queryFn: () => BrandGovernanceApiFactory.Apis.GetCompetitorBenchmark(params),
        ...options,
      }),
  },

  Schemas: {
    Response: {
      BrandOverview: BrandOverviewResponseSchema,
      CreativesScorecard: CreativesScorecardResponseSchema,
      LiveMonitor: LiveMonitorResponseSchema,
      CompetitorBenchmark: CompetitorBenchmarkResponseSchema,
    },
  },

  Apis: {
    GetBrandOverview: async (brandName: string, options?: ApiOptions): Promise<BrandOverviewResponse> => {
      const query = createQueryString({ brand_name: brandName });
      const url = `${createURLPath(BrandGovernanceApiFactory.Endpoint, "overview")}?${query}`;
      const response = await api(url, {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then((json) => BrandOverviewResponseSchema.parse(json));
    },

    GetCreativesScorecard: async (params: CreativesScorecardParams, options?: ApiOptions): Promise<CreativesScorecardResponse> => {
      const query = createQueryString({
        brand_name: params.brand_name,
        channels: params.channels?.length ? params.channels.join(",") : undefined,
        sub_brands: params.sub_brands?.length ? params.sub_brands.join(",") : undefined,
        period: params.period,
        start_date: params.start_date,
        end_date: params.end_date,
      });
      const url = `${createURLPath(BrandGovernanceApiFactory.Endpoint, "creatives", "scorecard")}?${query}`;
      const response = await api(url, {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then((json) => CreativesScorecardResponseSchema.parse(json));
    },

    GetLiveMonitor: async (params: LiveMonitorParams, options?: ApiOptions): Promise<LiveMonitorResponse> => {
      const query = createQueryString({ brand_name: params.brand_name });
      const url = `${createURLPath(BrandGovernanceApiFactory.Endpoint, "creatives", "live-monitor")}?${query}`;
      const response = await api(url, {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then((json) => LiveMonitorResponseSchema.parse(json));
    },

    GetCompetitorBenchmark: async (params: CompetitorBenchmarkParams, options?: ApiOptions): Promise<CompetitorBenchmarkResponse> => {
      const query = createQueryString(params);
      const url = `${createURLPath(BrandGovernanceApiFactory.Endpoint, "creatives", "competitor-benchmark")}?${query}`;
      const response = await api(url, {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      const json = await response.json();
      return CompetitorBenchmarkResponseSchema.parse(json);
    },
  },
};
